import { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  ArrowLeft, Calendar, Eye, Search, Clock, Share2, Copy,
  Linkedin, Twitter, Facebook, Mail, ChevronRight, ArrowRight
} from "lucide-react";
import DOMPurify from "dompurify";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;
const SITE_URL = window.location.origin;

const CATEGORY_COLORS = {
  "business-strategy": "bg-blue-50 text-blue-700 border-blue-200",
  "marketing-tips": "bg-pink-50 text-pink-700 border-pink-200",
  "ai-data-protection": "bg-violet-50 text-violet-700 border-violet-200",
  "industry-news": "bg-amber-50 text-amber-700 border-amber-200"
};

// Blog listing page
export const BlogPage = () => {
  const navigate = useNavigate();
  const [posts, setPosts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [activeCategory, setActiveCategory] = useState("");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => { fetchPosts(); fetchCategories(); }, []);

  const fetchPosts = async (cat = "") => {
    setLoading(true);
    const url = cat ? `${API}/api/blog/posts?category=${cat}` : `${API}/api/blog/posts`;
    try {
      const res = await fetch(url);
      if (res.ok) { const d = await res.json(); setPosts(d.posts || []); }
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  const fetchCategories = async () => {
    try {
      const res = await fetch(`${API}/api/blog/categories`);
      if (res.ok) { const d = await res.json(); setCategories(d.categories || []); }
    } catch (e) { console.error(e); }
  };

  const filterByCategory = (catId) => {
    setActiveCategory(catId === activeCategory ? "" : catId);
    fetchPosts(catId === activeCategory ? "" : catId);
  };

  const filtered = posts.filter(p => !search || p.title?.toLowerCase().includes(search.toLowerCase()) || p.excerpt?.toLowerCase().includes(search.toLowerCase()));

  return (
    <div className="min-h-screen bg-white">
      {/* Nav */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-md border-b border-neutral-100">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <img src="/mybiz-logo.png" alt="MBC" className="h-10 w-auto" />
            <div>
              <p className="text-sm font-semibold text-neutral-900 leading-tight">My Biz Consulting</p>
              <p className="text-[10px] text-[#39FF14] font-medium uppercase tracking-widest">Business Solutions Center</p>
            </div>
          </Link>
          <div className="flex items-center gap-3">
            <Link to="/pricing" className="text-sm text-neutral-600 hover:text-neutral-900 hidden sm:inline">Services</Link>
            <Link to="/clarity-assessment" className="text-sm text-neutral-600 hover:text-neutral-900 hidden sm:inline">Free Assessment</Link>
            <Button size="sm" variant="outline" onClick={() => navigate("/login")}>Login</Button>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="pt-24 pb-12 px-4 bg-gradient-to-b from-neutral-50 to-white">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-4xl sm:text-5xl font-bold text-neutral-900 mb-4">Business Insights</h1>
          <p className="text-lg text-neutral-600 mb-8">Strategy, growth tips, and industry insights from My Biz Consulting</p>
          <div className="relative max-w-md mx-auto">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" />
            <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search articles..." className="pl-9" />
          </div>
        </div>
      </section>

      {/* Categories */}
      <section className="px-4 pb-8">
        <div className="max-w-4xl mx-auto flex flex-wrap gap-2 justify-center">
          <Button size="sm" variant={!activeCategory ? "default" : "outline"} onClick={() => filterByCategory("")} className={!activeCategory ? "bg-neutral-900 text-white" : ""}>All</Button>
          {categories.map(cat => (
            <Button key={cat.id} size="sm" variant={activeCategory === cat.id ? "default" : "outline"} onClick={() => filterByCategory(cat.id)} className={activeCategory === cat.id ? "bg-neutral-900 text-white" : ""}>
              {cat.name} {cat.count > 0 && <span className="ml-1 text-xs opacity-60">({cat.count})</span>}
            </Button>
          ))}
        </div>
      </section>

      {/* Posts Grid */}
      <section className="px-4 pb-20">
        <div className="max-w-5xl mx-auto">
          {loading ? (
            <div className="text-center py-20"><div className="w-8 h-8 border-2 border-neutral-300 border-t-neutral-900 rounded-full animate-spin mx-auto" /></div>
          ) : filtered.length > 0 ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filtered.map(post => (
                <Card key={post.post_id} className="border hover:shadow-lg transition-shadow cursor-pointer group overflow-hidden" onClick={() => navigate(`/blog/${post.slug}`)}>
                  {post.featured_image && (
                    <div className="aspect-video overflow-hidden">
                      <img src={post.featured_image} alt={post.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                    </div>
                  )}
                  <CardContent className={post.featured_image ? "pt-4" : "pt-6"}>
                    <Badge variant="outline" className={`text-xs mb-3 ${CATEGORY_COLORS[post.category] || ""}`}>
                      {categories.find(c => c.id === post.category)?.name || post.category}
                    </Badge>
                    <h2 className="font-semibold text-neutral-900 mb-2 group-hover:text-[#39FF14] transition-colors line-clamp-2">{post.title}</h2>
                    <p className="text-sm text-neutral-500 line-clamp-2 mb-3">{post.excerpt}</p>
                    <div className="flex items-center justify-between text-xs text-neutral-400">
                      <span className="flex items-center gap-1"><Calendar className="w-3 h-3" />{post.published_at ? new Date(post.published_at).toLocaleDateString() : ""}</span>
                      <span className="flex items-center gap-1"><Eye className="w-3 h-3" />{post.views || 0} views</span>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <div className="text-center py-20 text-neutral-500">
              <p className="text-lg mb-2">No articles yet</p>
              <p className="text-sm">Check back soon for business insights and strategy tips.</p>
            </div>
          )}
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 bg-neutral-950 text-center">
        <p className="text-xs text-neutral-500">My Biz Consulting | Business Solutions Center</p>
      </footer>
    </div>
  );
};

// Single blog post page
export const BlogPostPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchPost = async () => {
      try {
        const res = await fetch(`${API}/api/blog/posts/${slug}`);
        if (res.ok) setPost(await res.json());
        else setPost(null);
      } catch (e) { setPost(null); }
      setLoading(false);
    };
    fetchPost();
  }, [slug]);

  const shareUrl = `${SITE_URL}/blog/${slug}`;

  const share = (platform) => {
    const title = encodeURIComponent(post?.title || "");
    const url = encodeURIComponent(shareUrl);
    const urls = {
      linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${url}`,
      twitter: `https://twitter.com/intent/tweet?text=${title}&url=${url}`,
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${url}`,
      email: `mailto:?subject=${title}&body=Check out this article: ${url}`
    };
    if (urls[platform]) window.open(urls[platform], "_blank");
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center"><div className="w-8 h-8 border-2 border-neutral-300 border-t-neutral-900 rounded-full animate-spin" /></div>;
  if (!post) return (
    <div className="min-h-screen flex items-center justify-center text-center px-4">
      <div>
        <h1 className="text-2xl font-bold mb-2">Article Not Found</h1>
        <Button onClick={() => navigate("/blog")} variant="outline"><ArrowLeft className="w-4 h-4 mr-2" />Back to Blog</Button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-white">
      {/* SEO Meta — rendered via React Helmet or document.title */}
      <script dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(`document.title = "${post.title} | My Biz Consulting Blog"`) }} />

      {/* Nav */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-md border-b border-neutral-100">
        <div className="max-w-4xl mx-auto px-4 h-16 flex items-center justify-between">
          <Link to="/blog" className="flex items-center gap-2 text-neutral-600 hover:text-neutral-900 text-sm">
            <ArrowLeft className="w-4 h-4" /> Back to Blog
          </Link>
          <Link to="/" className="flex items-center gap-2">
            <img src="/mybiz-logo.png" alt="MBC" className="h-8 w-auto" />
            <span className="text-xs text-[#39FF14] font-medium uppercase tracking-wider hidden sm:inline">BSC</span>
          </Link>
        </div>
      </nav>

      {/* Article */}
      <article className="pt-24 pb-16 px-4">
        <div className="max-w-3xl mx-auto">
          {/* Header */}
          <Badge variant="outline" className={`text-xs mb-4 ${CATEGORY_COLORS[post.category] || ""}`}>
            {post.category?.replace(/-/g, " ").replace(/\b\w/g, c => c.toUpperCase())}
          </Badge>
          <h1 className="text-3xl sm:text-4xl font-bold text-neutral-900 mb-4 leading-tight">{post.title}</h1>
          <div className="flex items-center gap-4 text-sm text-neutral-500 mb-8">
            <span>By <Link to="/jamilah-n-lawry" className="text-neutral-700 hover:text-[#2BC40E] font-medium">{post.author_name || "Jamilah N. Lawry"}</Link></span>
            <span className="flex items-center gap-1"><Calendar className="w-3.5 h-3.5" />{post.published_at ? new Date(post.published_at).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }) : ""}</span>
            <span className="flex items-center gap-1"><Eye className="w-3.5 h-3.5" />{post.views || 0} views</span>
          </div>

          {/* Featured Image */}
          {post.featured_image && (
            <img src={post.featured_image} alt={post.title} className="w-full rounded-xl mb-8 border" />
          )}

          {/* Content */}
          <div className="prose prose-lg max-w-none prose-headings:text-neutral-900 prose-p:text-neutral-700 prose-a:text-[#39FF14] prose-strong:text-neutral-900" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(post.content) }} />

          {/* SEO Keywords */}
          {post.seo_keywords?.length > 0 && (
            <div className="mt-8 pt-6 border-t flex flex-wrap gap-2">
              {post.seo_keywords.map((kw, i) => <span key={i} className="text-xs bg-neutral-100 text-neutral-600 px-3 py-1 rounded-full">{kw}</span>)}
            </div>
          )}

          {/* Share Bar */}
          <div className="mt-8 pt-6 border-t">
            <p className="text-sm font-medium text-neutral-900 mb-3">Share this article</p>
            <div className="flex flex-wrap gap-2">
              <Button size="sm" variant="outline" onClick={() => share("linkedin")} className="text-xs"><Linkedin className="w-4 h-4 mr-1" />LinkedIn</Button>
              <Button size="sm" variant="outline" onClick={() => share("twitter")} className="text-xs"><Twitter className="w-4 h-4 mr-1" />Twitter / X</Button>
              <Button size="sm" variant="outline" onClick={() => share("facebook")} className="text-xs"><Facebook className="w-4 h-4 mr-1" />Facebook</Button>
              <Button size="sm" variant="outline" onClick={() => share("email")} className="text-xs"><Mail className="w-4 h-4 mr-1" />Email</Button>
              <Button size="sm" variant="outline" onClick={() => { navigator.clipboard.writeText(shareUrl); toast.success("Link copied!"); }} className="text-xs"><Copy className="w-4 h-4 mr-1" />Copy Link</Button>
            </div>
          </div>

          {/* Branding */}
          <div className="mt-10 p-6 bg-neutral-50 rounded-xl text-center border">
            <img src="/mybiz-logo.png" alt="MBC" className="h-10 w-auto mx-auto mb-3" />
            <p className="text-sm text-neutral-600 mb-4">Want strategic guidance for your business?</p>
            <div className="flex gap-3 justify-center">
              <Button size="sm" onClick={() => navigate("/clarity-assessment")} className="bg-neutral-900 text-white">Free Business Assessment</Button>
              <Button size="sm" variant="outline" onClick={() => navigate("/community")}>Join the Community</Button>
            </div>
            <p className="text-xs text-neutral-400 mt-4">Powered by My Biz Consulting - Business Solutions Center</p>
          </div>
        </div>
      </article>
    </div>
  );
};
